import React, { useState, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface Message {
  role: 'user' | 'bot';
  text: string;
  sources?: Array<{ pdf: string; page: number }>;
  timestamp?: Date;
}

const ChatbotPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    { 
      role: 'bot', 
      text: 'Hello! Ask me anything about the research papers and documentation in our knowledge base.',
      timestamp: new Date()
    },
  ]);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!isAuthenticated) {
    return (
      <section className="feature-page">
        <h1>🤖 ECOVYA CHATBOT</h1>
        <p>You must sign in to use the chatbot. Sign in so we can track your questions and show your search history.</p>
        <div className="auth-required-panel">
          <NavLink to="/login" className="auth-link-btn">
            Sign in to continue
          </NavLink>
        </div>
      </section>
    );
  }

  const onSend = async (event: React.FormEvent) => {
    event.preventDefault();
    const text = input.trim();
    if (!text || loading) {
      return;
    }

    // Add user message
    setMessages((prev) => [...prev, { role: 'user', text, timestamp: new Date() }]);
    setInput('');
    setLoading(true);

    try {
      const response = await api.post('/chatbot/query', { question: text });
      const { response: botResponse, sources } = response.data;

      setMessages((prev) => [
        ...prev,
        {
          role: 'bot',
          text: botResponse,
          sources: sources,
          timestamp: new Date(),
        },
      ]);
    } catch (error: any) {
      const errorMsg = error?.response?.data?.detail || 'Failed to get response. Please try again.';
      setMessages((prev) => [
        ...prev,
        { role: 'bot', text: errorMsg, timestamp: new Date() },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="feature-page">
      <h1>🤖 ECOVYA CHATBOT</h1>
      <p>Ask questions about our research papers and documentation.</p>

      {/* Chat Interface */}
      <div className="chat-panel">
        {messages.map((message, idx) => (
          <div key={idx} className={`chat-message ${message.role}`}>
            <div className="chat-bubble">
              <p>{message.text}</p>
              {message.timestamp && (
                <span className="timestamp">
                  {message.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="chat-message bot">
            <div className="chat-bubble loading-bubble">
              <span className="loading-dots">●●●</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Form */}
      <form className="chat-form" onSubmit={onSend}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask a question..."
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
        >
          {loading ? 'Thinking...' : 'Send'}
        </button>
      </form>
    </section>
  );
};

export default ChatbotPage;
